import { useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { useIsHovered } from '@/hooks/useIsHovered';
import CopyBtn from '@/components/Common/ImageList/CopyBtnV2';
import { copyImage } from '@/utils/files';
import { useResolvedAssets } from '@/state/workflow.state';
import { PlaceholderImage } from './ParamsDisplayComponents';
import type { ImageInput } from './galleryParamsUtils';
import type { MediaAsset } from '@/types/api/assets';

const COPY_EVENT_TRACKING = { name: 'gallery_params_copied_ref_image', payload: {} };

const ReferenceImageThumbnail = ({ url, draggable = true }: { url: string; draggable?: boolean }) => {
  const [imgError, setImgError] = useState(false);

  if (imgError) return <PlaceholderImage />;

  return (
    <img
      src={url}
      alt="Reference image"
      width="56px"
      height="56px"
      draggable={draggable}
      style={{ borderRadius: '4px', objectFit: 'cover' }}
      onError={() => setImgError(true)}
    />
  );
};

const CopyImageButton = ({ url }: { url: string }) => {
  const { t } = useTranslation();
  const resolvedAssets = useResolvedAssets();
  const handleCopy = () => {
    void copyImage({ type: 'image', url } as MediaAsset, resolvedAssets);
  };

  return (
    <CopyBtn
      onClick={handleCopy}
      icon="copy"
      size="medium"
      title={t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.COPY_IMAGE)}
      eventTracking={COPY_EVENT_TRACKING}
    />
  );
};

const DraggableReference = ({
  image,
  onDragStart,
  onDragEnd,
}: {
  image: ImageInput;
  onDragStart?: (event: DragEvent<HTMLElement>, image: ImageInput) => void;
  onDragEnd?: (event: DragEvent<HTMLElement>) => void;
}) => {
  const url = image.thumbnailUrl || image.url;
  if (!onDragStart) return <ReferenceImageThumbnail url={url} />;

  return (
    <div
      draggable
      onDragStart={(event) => onDragStart(event, image)}
      onDragEnd={onDragEnd}
      style={{ cursor: 'grab', width: 'fit-content' }}
    >
      {/* The wrapper is the drag source, so the picture itself must not start a native image drag. */}
      <ReferenceImageThumbnail url={url} draggable={false} />
    </div>
  );
};

const SingleImageLayout = ({
  label,
  image,
  onDragStart,
  onDragEnd,
}: {
  label: string;
  image: ImageInput;
  onDragStart?: (event: DragEvent<HTMLElement>, image: ImageInput) => void;
  onDragEnd?: (event: DragEvent<HTMLElement>) => void;
}) => {
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  const url = image.thumbnailUrl || image.url;

  return (
    <FlexCol sx={{ gap: 0.5, pb: 1 }}>
      <FlexCenVer
        sx={{ justifyContent: 'space-between', height: '24px', py: 0.5, boxSizing: 'border-box' }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <TextSmRg>{label}</TextSmRg>
        {isHovered && <CopyImageButton url={url} />}
      </FlexCenVer>
      <DraggableReference image={image} onDragStart={onDragStart} onDragEnd={onDragEnd} />
    </FlexCol>
  );
};

const MultiImageRow = ({
  image,
  onDragStart,
  onDragEnd,
}: {
  image: ImageInput;
  onDragStart?: (event: DragEvent<HTMLElement>, image: ImageInput) => void;
  onDragEnd?: (event: DragEvent<HTMLElement>) => void;
}) => {
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  const url = image.thumbnailUrl || image.url;

  return (
    <FlexCenVer
      sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <DraggableReference image={image} onDragStart={onDragStart} onDragEnd={onDragEnd} />
      {isHovered && (
        <FlexCenVer sx={{ gap: 0.5 }}>
          <CopyImageButton url={url} />
        </FlexCenVer>
      )}
    </FlexCenVer>
  );
};

interface ReferenceImagesProps {
  imageInputs: ImageInput[];
  label: string;
  /** When set, each thumbnail can be dragged the same way as the asset preview. */
  onDragStart?: (event: DragEvent<HTMLElement>, image: ImageInput) => void;
  onDragEnd?: (event: DragEvent<HTMLElement>) => void;
}

const ReferenceImages = ({ imageInputs, label, onDragStart, onDragEnd }: ReferenceImagesProps) => {
  if (imageInputs.length === 1) {
    return <SingleImageLayout label={label} image={imageInputs[0]} onDragStart={onDragStart} onDragEnd={onDragEnd} />;
  }

  return (
    <FlexCol sx={{ gap: 0.5, pb: 1 }}>
      <TextSmRg sx={{ py: 0.5, height: '24px', boxSizing: 'border-box' }}>{label}</TextSmRg>
      <FlexCol sx={{ gap: 0.5 }}>
        {imageInputs.map((image) => (
          <MultiImageRow key={image.id ?? image.order} image={image} onDragStart={onDragStart} onDragEnd={onDragEnd} />
        ))}
      </FlexCol>
    </FlexCol>
  );
};

export default ReferenceImages;
